import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import CONFIG from "@/config/env";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBase = CONFIG.API_BASE;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const stateRaw = params.get("state");
    const oauthError = params.get("error");

    let invite: string | null = null;
    let returnTo: string | null = null;
    if (stateRaw) {
      try {
        const normalized = stateRaw.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        const decoded = JSON.parse(atob(padded));
        invite = decoded?.invite || null;
        returnTo = decoded?.returnTo || null;
      } catch {
        invite = stateRaw;
      }
    }

    if (oauthError) {
      toast.error(`Google authentication failed: ${oauthError}`);
      navigate("/auth/login");
      return;
    }

    if (code) {
      handleCallback(code, invite, returnTo);
    } else {
      navigate("/auth/login");
    }
  }, [location]);

  const handleCallback = async (code: string, invite: string | null, returnTo: string | null) => {
    try {
      const response = await fetch(`${apiBase}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code, invite }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("genofx:token", data.token);
        if (data.accessToken) {
          localStorage.setItem("genofx:accessToken", data.accessToken);
        }
        if (data.user?.sessionId || data.sessionId) {
          localStorage.setItem("genofx:sessionId", data.user?.sessionId || data.sessionId);
        }
        localStorage.setItem("genofx:refreshToken", data.refreshToken);
        localStorage.setItem("genofx:username", data.username || "");
        localStorage.setItem("genofx:user", JSON.stringify(data.user));
        localStorage.setItem("genofx:email", data.user?.email || "");
        toast.success("Login successful");
        const safeReturnTo = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/auth') ? returnTo : '/';
        const appUrl = CONFIG.APP_URL;
        const target = new URL(safeReturnTo, appUrl);
        target.searchParams.set('token', data.token);
        sessionStorage.removeItem('genofx:returnTo');
        window.location.href = target.toString();
      } else {
        toast.error(data.message || "Authentication failed");
        navigate("/auth/login");
      }
    } catch (error) {
      console.error("Auth callback error:", error);
      toast.error("An error occurred during authentication");
      navigate("/auth/login");
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-background text-foreground">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-lg font-medium">Authenticating...</p>
      </div>
    </div>
  );
}
